/**
 * TransactionType Query Key Factory
 *
 * Centralized query key factory for React Query cache management.
 * Provides consistent, type-safe query keys for all transaction_type queries.
 *
 * Query keys follow a hierarchical structure for efficient cache invalidation:
 * - ['transactiontypes'] - All transaction_type queries
 * - ['transactiontypes', 'list'] - All list queries
 * - ['transactiontypes', 'list', { filters }] - Specific filtered list
 * - ['transactiontypes', 'detail', id] - Specific transaction_type by ID
 *
 * @example
 * ```typescript
 * import { transaction_typeKeys } from './queryKeys';
 *
 * // Use in useQuery
 * const { data } = useQuery({
 *   queryKey: transaction_typeKeys.list({ is_active: true }),
 *   queryFn: () => transaction_typeService.getAll({ is_active: true }),
 * });
 *
 * // Invalidate all transaction_type queries
 * queryClient.invalidateQueries({ queryKey: transaction_typeKeys.all });
 *
 * // Invalidate specific transaction_type
 * queryClient.invalidateQueries({ queryKey: transaction_typeKeys.detail('uuid') });
 * ```
 *
 * Generated by CodeGen OS v1.0.0
 * Generated at: 2026-04-25T14:23:22.154243Z
 */

import type { TransactionTypeFilterOptions } from '../types/transaction_type.types';

// ---- CODAIC:CUSTOM IMPORTS ----
// Add custom imports below. This section is preserved across regeneration.
// ---- CODAIC:END CUSTOM ----

/**
 * Root query key for TransactionType entity
 * Used as namespace prefix for all transaction_type queries
 */
const TRANSACTION_TYPE_ROOT = 'transactiontypes' as const;

/**
 * TransactionType Query Keys
 *
 * Hierarchical query key factory for transaction_type queries:
 * - all: Base key for all transaction_type queries
 * - lists: All list queries (for bulk invalidation)
 * - list(filters): Specific filtered list
 * - details: All detail queries
 * - detail(id): Specific transaction_type by ID
 * - dropdown(activeOnly): Dropdown options query
 *
 * @example
 * ```typescript
 * // List with filters
 * const key = transaction_typeKeys.list({ is_active: true });
 * // Result: ['transactiontypes', 'list', { is_active: true }]
 *
 * // Detail by ID
 * const key = transaction_typeKeys.detail('uuid-here');
 * // Result: ['transactiontypes', 'detail', 'uuid-here']
 *
 * // Invalidate all transaction_type data
 * queryClient.invalidateQueries({ queryKey: transaction_typeKeys.all });
 * ```
 */
export const transaction_typeKeys = {
  /**
   * Base key for all transaction_type queries
   * Use this to invalidate all transaction_type-related queries at once
   *
   * @example
   * ```typescript
   * // Invalidate everything after a major change
   * queryClient.invalidateQueries({ queryKey: transaction_typeKeys.all });
   * ```
   */
  all: [TRANSACTION_TYPE_ROOT] as const,

  /**
   * Key for all list queries
   * Use this to invalidate all list queries without affecting detail queries
   *
   * @returns Query key tuple for list namespace
   *
   * @example
   * ```typescript
   * // Invalidate all list queries
   * queryClient.invalidateQueries({ queryKey: transaction_typeKeys.lists() });
   * ```
   */
  lists: () => [...transaction_typeKeys.all, 'list'] as const,

  /**
   * Key for a specific filtered list query
   * Include filters in the key to enable automatic re-fetching when filters change
   *
   * @param filters - Optional filter options for the query
   * @returns Query key tuple including filters
   *
   * @example
   * ```typescript
   * // List all active
   * const key = transaction_typeKeys.list({ is_active: true });
   *
   * // List with search
   * const key = transaction_typeKeys.list({ search: 'test' });
   *
   * // Unfiltered list
   * const key = transaction_typeKeys.list();
   * ```
   */
  list: (filters?: TransactionTypeFilterOptions) =>
    [...transaction_typeKeys.lists(), filters ?? {}] as const,

  /**
   * Key for all detail queries
   * Use this to invalidate all detail queries without affecting list queries
   *
   * @returns Query key tuple for detail namespace
   *
   * @example
   * ```typescript
   * // Invalidate all detail queries
   * queryClient.invalidateQueries({ queryKey: transaction_typeKeys.details() });
   * ```
   */
  details: () => [...transaction_typeKeys.all, 'detail'] as const,

  /**
   * Key for a specific transaction_type by ID
   * Use this for fetching and invalidating individual transaction_type records
   *
   * @param id - TransactionType ID (UUID)
   * @returns Query key tuple for specific transaction_type
   *
   * @example
   * ```typescript
   * // Fetch specific transaction_type
   * const { data } = useQuery({
   *   queryKey: transaction_typeKeys.detail('uuid-here'),
   *   queryFn: () => transaction_typeService.getById('uuid-here'),
   * });
   *
   * // Invalidate after update
   * queryClient.invalidateQueries({
   *   queryKey: transaction_typeKeys.detail('uuid-here')
   * });
   * ```
   */
  detail: (id: string) => [...transaction_typeKeys.details(), id] as const,

  /**
   * Key for dropdown options query
   * Separate key allows independent caching of dropdown data
   *
   * @param activeOnly - Whether to include only active transaction_types (default: true)
   * @returns Query key tuple for dropdown query
   *
   * @example
   * ```typescript
   * // Active items only (default)
   * const key = transaction_typeKeys.dropdown();
   *
   * // Include inactive items
   * const key = transaction_typeKeys.dropdown(false);
   * ```
   */
  dropdown: (activeOnly: boolean = true) =>
    [...transaction_typeKeys.all, 'dropdown', { activeOnly }] as const,

  /**
   * Key for transaction_type by code lookup
   * Use this when fetching by business code instead of UUID
   *
   * @param code - TransactionType code (e.g., 'USD', 'EUR')
   * @returns Query key tuple for code lookup
   *
   * @example
   * ```typescript
   * const { data } = useQuery({
   *   queryKey: transaction_typeKeys.byCode('USD'),
   *   queryFn: () => transaction_typeService.getByCode('USD'),
   * });
   * ```
   */
  byCode: (code: string) =>
    [...transaction_typeKeys.all, 'by-code', code.toUpperCase()] as const,

  /**
   * TransactionTypes filtered by service line ID
   *
   * @param serviceLineId - ServiceLine ID (UUID)
   * @returns Query key tuple
   */
  byServiceLine: (serviceLineId: string) =>
    [...transaction_typeKeys.all, 'by-service-line', serviceLineId] as const,

  /**
   * TransactionTypes filtered by phase template ID
   *
   * @param phaseTemplateId - PhaseTemplate ID (UUID)
   * @returns Query key tuple
   */
  byPhaseTemplate: (phaseTemplateId: string) =>
    [...transaction_typeKeys.all, 'by-phase-template', phaseTemplateId] as const,
} as const;

/**
 * Type representing any valid TransactionType query key
 * Useful for typing query key arrays in generic contexts
 */
export type TransactionTypeQueryKey =
  | typeof transaction_typeKeys.all
  | ReturnType<typeof transaction_typeKeys.lists>
  | ReturnType<typeof transaction_typeKeys.list>
  | ReturnType<typeof transaction_typeKeys.details>
  | ReturnType<typeof transaction_typeKeys.detail>
  | ReturnType<typeof transaction_typeKeys.dropdown>
  | ReturnType<typeof transaction_typeKeys.byCode>
;

// ---- CODAIC:CUSTOM HOOKS ----
// Add custom query key factories below. This section is preserved across regeneration.
// ---- CODAIC:END CUSTOM ----

/**
 * Default export for convenience
 */
export default transaction_typeKeys;
